import { useState } from 'react'
import { ChevronDown, Loader2, PlayCircle } from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Typography } from '@/components/ui/typography'
import { cn } from '@/lib/utils'
import { useTriggerBackfill, useTriggerSyncFull } from '@/hooks/use-chronograph'

interface SyncActionsProps {
  onSyncTriggered?: () => void
}

export function SyncActions({ onSyncTriggered }: SyncActionsProps) {
  const currentYear = new Date().getUTCFullYear()
  const yearOptions = Array.from(
    { length: currentYear - 2010 + 1 },
    (_, index) => currentYear - index
  )
  const [backfillStartYear, setBackfillStartYear] = useState(
    Math.max(2018, currentYear - 8)
  )
  const [backfillEndYear, setBackfillEndYear] = useState(currentYear)
  const [confirmSync, setConfirmSync] = useState(false)
  const [confirmBackfill, setConfirmBackfill] = useState(false)

  const triggerFull = useTriggerSyncFull()
  const triggerBackfill = useTriggerBackfill()

  const canRunBackfill = backfillStartYear <= backfillEndYear

  const handleConfirmSync = () => {
    triggerFull.mutate(undefined, {
      onSuccess: () => onSyncTriggered?.(),
    })
    setConfirmSync(false)
  }

  const handleConfirmBackfill = () => {
    if (!canRunBackfill) return
    triggerBackfill.mutate(
      {
        startYear: backfillStartYear,
        endYear: backfillEndYear,
      },
      { onSuccess: () => onSyncTriggered?.() }
    )
    setConfirmBackfill(false)
  }

  const [isOpen, setIsOpen] = useState(false)

  return (
    <>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <div
          className={cn(
            'rounded-xl border border-primary/12 bg-primary/[0.015] transition-colors',
            !isOpen && 'hover:bg-primary/[0.025]'
          )}
        >
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="flex w-full cursor-pointer items-center justify-between px-4 py-2.5"
            >
              <div className="flex items-center gap-2">
                <PlayCircle className="size-4 text-primary/70" />
                <Typography variant="small" className="text-primary/70">
                  Manual Syncs
                </Typography>
              </div>
              <ChevronDown
                className={cn(
                  'size-4 text-primary/70 transition-transform duration-200',
                  isOpen && 'rotate-180'
                )}
              />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="grid gap-4 border-t border-primary/10 px-4 py-4 sm:grid-cols-2">
              {/* Sync Last 6 Months */}
              <div className="rounded-lg border border-primary/10 bg-background px-4 py-3.5">
                <Typography variant="small">Sync Last 6 Months</Typography>
                <Typography variant="body-sm" className="mt-1 text-primary/70">
                  Companies, investments, and recent financial metrics.
                </Typography>
                <Button
                  variant="outline"
                  className="mt-3"
                  onClick={() => setConfirmSync(true)}
                  disabled={triggerFull.isPending}
                >
                  {triggerFull.isPending ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <PlayCircle className="size-4" />
                  )}
                  Run Sync
                </Button>
              </div>

              {/* Historical Backfill */}
              <div className="rounded-lg border border-primary/10 bg-background px-4 py-3.5">
                <Typography variant="small">Historical Backfill</Typography>
                <Typography variant="body-sm" className="mt-1 text-primary/70">
                  Quarter-by-quarter import for a selected year range.
                </Typography>
                <div className="mt-3 flex items-center gap-2">
                  <Select
                    value={String(backfillStartYear)}
                    onValueChange={(v) => setBackfillStartYear(Number(v))}
                  >
                    <SelectTrigger
                      aria-label="Backfill start year"
                      className="h-9 w-[90px] cursor-pointer border-primary/15 bg-background"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {yearOptions.map((year) => (
                        <SelectItem key={`s-${year}`} value={String(year)}>
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Typography variant="body-sm" className="text-primary/70">
                    &ndash;
                  </Typography>
                  <Select
                    value={String(backfillEndYear)}
                    onValueChange={(v) => setBackfillEndYear(Number(v))}
                  >
                    <SelectTrigger
                      aria-label="Backfill end year"
                      className="h-9 w-[90px] cursor-pointer border-primary/15 bg-background"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {yearOptions.map((year) => (
                        <SelectItem key={`e-${year}`} value={String(year)}>
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    onClick={() => setConfirmBackfill(true)}
                    disabled={triggerBackfill.isPending || !canRunBackfill}
                  >
                    {triggerBackfill.isPending ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <PlayCircle className="size-4" />
                    )}
                    Run Backfill
                  </Button>
                </div>
                {!canRunBackfill ? (
                  <Typography
                    variant="body-sm"
                    className="mt-2 text-destructive"
                  >
                    Start year must be &le; end year.
                  </Typography>
                ) : null}
              </div>
            </div>
          </CollapsibleContent>
        </div>
      </Collapsible>

      {/* Sync confirmation */}
      <AlertDialog open={confirmSync} onOpenChange={setConfirmSync}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Run full sync?</AlertDialogTitle>
            <AlertDialogDescription>
              This will sync companies, investments, and financial metrics for
              the last 6 months from Chronograph. It may take a few minutes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmSync}>
              Run Sync
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Backfill confirmation */}
      <AlertDialog open={confirmBackfill} onOpenChange={setConfirmBackfill}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Run historical backfill?</AlertDialogTitle>
            <AlertDialogDescription>
              This will import quarter-by-quarter financial data from{' '}
              {backfillStartYear} to {backfillEndYear}. Backfills can take
              significantly longer than a regular sync.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmBackfill}>
              Run Backfill
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
